// File: typescript/apps/front-end/src/main/react/discussion/DiscussionRail.tsx
//
// Right-hand rail for the Discussion page: three independently-hydrated modules
// (Related discussions, Groups this discussion is in, Series it is part of). The discussion
// content + arguments are the critical path and render first; these modules fire their own
// requests after mount, each owning its loading/empty/error state, with skeletons that reserve
// the final card height so the rail never reflows.
//
// Desktop-only: the rail is hidden below the lg breakpoint and does NOT issue any requests
// there (mobile gets no rail and no rail backend calls). On desktop it sticks just below the top
// nav and scrolls independently of the page (same pattern as the Home rail).
import React from "react";
import { Layers, MessageSquare, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Localizer } from "@/main/react/Types";
import { LocalizationKey } from "@/main/util/LocalizationUtil";
import { ClientFactory } from "@/main/common/ClientFactory";
import { getFriendlyCountBadgeLabel } from "@/main/react/card/CardMetadataBadgeCopy";
import { DiscussionWithoutContentDTO, GroupDTO, SeriesDTO } from "@/generated-client";
import { RecommendedGroupCard } from "@/main/react/page/home-page/HomeDiscoveryRail";
import { HOME_RAIL_ROW_LIST_CLASS } from "@/main/react/page/home-page/HomePageShared";
import { onDiscoveryCardActivated } from "@/main/analytics/discoveryAttribution";
import { useDiscoveryRailImpression } from "@/main/react/hooks/useDiscoveryRailImpression";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import SeriesButtons from "@/main/react/series/SeriesButtons";
import DiscussionBookmarkToggle from "@/main/react/discussion/DiscussionBookmarkToggle";
import DiscussionVoteButtons from "@/main/react/discussion/DiscussionVoteButtons";
import { getUserFacingSeriesTitle } from "@/main/react/series/BookmarksSeriesLocalization";
import { getDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import SafeImage from "@/main/util/SafeImage";
import { THUMBNAIL_IMAGE_SIZE } from "@/main/util/ImageUrlHelper";
import { parseDiscussionCardMediaItems } from "@/main/react/card/DiscussionCardMedia";
import { crawlableDiscussionListFallback, SsrRestoredList } from "@/main/react/list/ssrFallbacks";

// How many items each rail module shows: 10 related discussions, 10 groups, 10 series.
const RAIL_PAGE_SIZE = 10;
const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)";
// Matches the Home rail's reveal/settle duration so the open animation feels identical.
const DISCUSSION_RAIL_REVEAL_DURATION_MS = 220;

// Shared chrome copied from the Home rail so the Discussion rail is visually identical.
//
// `DISCUSSION-6`, 2026-08-20: the border alphas here were `/50` and `/55` and are now full-strength
// `border-border`. The design language makes borders the ONLY elevation mechanism, and diluting the
// token undoes the 2026-08-17 raise: composited on `--card` in light, `/50` measures 1.77:1 and `/75`
// 2.49:1, all under the 3:1 the raise was for. `HomeDiscoveryRail.tsx` still carries the diluted twin
// of these two classes, so the sentence above is aspirational until it converges too — that is
// recorded as owed in
// `designs/07-archive/runs/2026-08-20-ux-critique-remediation-wave-6/batch-H-discussion-page.md`.
const MODULE_ICON_TILE_CLASS =
    "mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full border border-border bg-muted/70 text-text-main shadow-[inset_0_1px_0_rgba(255,255,255,0.22)] dark:shadow-none";
const RAIL_ROW_CLASS =
    "block rounded-2xl border border-border bg-muted/55 p-3 card-hover";

type DiscussionRailTabKey = "related" | "groups" | "series";

type LoadableItems<T> = {
    items: T[];
    loading: boolean;
    error: boolean;
};

function toFiniteCount(value: unknown): number {
    const parsed = Number(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
}

// --- viewport gate ---------------------------------------------------------

function isDesktopViewport(): boolean {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return true;
    }
    return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
}

export function useIsDesktopViewport(): boolean {
    const [isDesktop, setIsDesktop] = React.useState<boolean>(() => isDesktopViewport());

    React.useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
            return;
        }
        const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
        const update = (event?: MediaQueryListEvent) => setIsDesktop(event?.matches ?? mediaQuery.matches);
        update();
        if (typeof mediaQuery.addEventListener === "function") {
            mediaQuery.addEventListener("change", update);
            return () => mediaQuery.removeEventListener("change", update);
        }
        mediaQuery.addListener(update);
        return () => mediaQuery.removeListener(update);
    }, []);

    return isDesktop;
}

// --- hooks -----------------------------------------------------------------

function useAsyncList<T>(
    enabled: boolean,
    discussionPublicId: string | undefined,
    fetcher: (id: string) => Promise<T[]>,
    initialItems: T[] = [],
): LoadableItems<T> & { reload: () => void } {
    const active = enabled && Boolean(discussionPublicId);
    const [state, setState] = React.useState<LoadableItems<T>>({
        items: initialItems,
        loading: active,
        error: false,
    });
    const [reloadToken, setReloadToken] = React.useState(0);

    React.useEffect(() => {
        if (!active || !discussionPublicId) {
            // Not on desktop (or no discussion) — never fetch.
            setState({ items: initialItems, loading: false, error: false });
            return;
        }

        let cancelled = false;
        setState((previous) => ({ items: previous.items, loading: true, error: false }));

        void fetcher(discussionPublicId)
            .then((items) => {
                if (cancelled) return;
                setState({ items: items ?? [], loading: false, error: false });
            })
            .catch(() => {
                if (cancelled) return;
                setState({ items: [], loading: false, error: true });
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, discussionPublicId, reloadToken]);

    const reload = React.useCallback(() => setReloadToken((token) => token + 1), []);
    return { ...state, reload };
}

/**
 * Which corpus the Related module is actually showing.
 *
 * `DISCUSSION-1` (2026-08-14): on `/related` the rail headed eleven cards "Related posts / Other
 * takes connected to this one." while the main column, on the same screen, said "No related posts
 * yet". Both read the same endpoint; the rail silently substituted the site's most active posts and
 * kept the label. The settled decision is to relabel while the fallback is serving, under one
 * binding constraint: the rail and the panel must not be ABLE to disagree.
 *
 * So the label is not a second string chosen by a second condition — two things that can drift. The
 * corpus tag is written where the corpus is CHOSEN, travels with every row, and the heading is
 * derived from the rows the module is about to draw (`resolveRelatedRailSource` +
 * `RELATED_RAIL_MODULE_COPY`). There is no other path to a heading, so the only way to render the
 * related label is to hold rows the related endpoint itself returned. Design §13.1.
 */
export type RelatedRailSource = "related" | "fallback";

export type RelatedRailRow = {
    source: RelatedRailSource;
    discussion: DiscussionWithoutContentDTO;
};

/**
 * Total, by construction: every source has exactly one copy pair and the component holds no title
 * literal of its own. Add a source and this map stops compiling until it is given copy.
 */
export const RELATED_RAIL_MODULE_COPY: Record<RelatedRailSource, { titleKey: LocalizationKey; subtitleKey: LocalizationKey }> = {
    related: {
        titleKey: "discussion.rail.related.title",
        subtitleKey: "discussion.rail.related.subtitle",
    },
    fallback: {
        titleKey: "discussion.rail.active.title",
        subtitleKey: "discussion.rail.active.subtitle",
    },
};

/**
 * The heading is a property of the ROWS, never of the module. Any fallback row present forces the
 * fallback heading, so the module can never over-claim what it is showing. (The fetcher returns one
 * corpus or the other, so a mixed list cannot occur — this is the belt to that braces.)
 */
export function resolveRelatedRailSource(rows: readonly RelatedRailRow[]): RelatedRailSource {
    return rows.some((row) => row.source === "fallback") ? "fallback" : "related";
}

function useRelatedDiscussions(enabled: boolean, discussionPublicId: string | undefined) {
    return useAsyncList<RelatedRailRow>(enabled, discussionPublicId, async (id) => {
        const api = ClientFactory.getAjaxPaginationRestControllerApi();
        const related = (await api.relatedDiscussions(id, undefined, undefined, RAIL_PAGE_SIZE)).data ?? [];
        if (related.length > 0) {
            return related.slice(0, RAIL_PAGE_SIZE).map((discussion) => ({ source: "related" as const, discussion }));
        }
        // No computed related discussions for this one — fall back to the site's most active
        // discussions (excluding this discussion) so the module is never empty. The rows carry the
        // tag that relabels the module: this branch is the ONLY producer of `"fallback"`.
        const active = (await api.activeDiscussionsPage()).data ?? [];
        return active
            .filter((discussion) => discussion.publicId !== id)
            .slice(0, RAIL_PAGE_SIZE)
            .map((discussion) => ({ source: "fallback" as const, discussion }));
    });
}

function useDiscussionGroupsRail(enabled: boolean, discussionPublicId: string | undefined, seedGroups: GroupDTO[]) {
    return useAsyncList<GroupDTO>(
        enabled,
        discussionPublicId,
        async (id) => {
            const response = await ClientFactory.getAjaxPaginationRestControllerApi().groupsForDiscussion(id);
            return (response.data ?? []).slice(0, RAIL_PAGE_SIZE);
        },
        seedGroups,
    );
}

function useDiscussionSeries(enabled: boolean, discussionPublicId: string | undefined) {
    return useAsyncList<SeriesDTO>(enabled, discussionPublicId, async (id) => {
        const response = await ClientFactory.getAjaxPaginationRestControllerApi().seriesForDiscussion(id);
        return (response.data ?? []).slice(0, RAIL_PAGE_SIZE);
    });
}

// --- shared presentation ---------------------------------------------------

// `tile` reserves the leading 48px slot the loaded rows render. It must match the loaded row's shape,
// not just its presence: a circular placeholder under a squared thumbnail is the same reflow in a
// different disguise. "none" is for modules whose rows genuinely have no tile.
function RailSkeletonRows(props: { rows?: number; tile?: "circle" | "rounded" | "none" }) {
    const { rows = 3, tile = "circle" } = props;
    return (
        <div className="space-y-3" aria-hidden="true">
            {Array.from({ length: rows }).map((_, index) => (
                <div key={index} className="rounded-2xl border border-border bg-muted/40 p-3">
                    <div className="flex items-start gap-3">
                        {tile !== "none" ? (
                            <div className={cn("size-12 shrink-0 animate-pulse bg-accent", tile === "rounded" ? "rounded-xl" : "rounded-full")} />
                        ) : null}
                        <div className="min-w-0 flex-1 space-y-2">
                            <div className="h-3.5 w-3/4 animate-pulse rounded bg-accent" />
                            <div className="h-3 w-1/2 animate-pulse rounded bg-accent" />
                            <div className="h-3 w-2/3 animate-pulse rounded bg-accent" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

// One collapsible rail module, rendered as an accordion panel. This mirrors the Home rail's
// `data-home-reveal-*` chrome exactly so both rails share the same open/close animation and the
// same (now closed-state) accent tint defined in 01-base.css.
function DiscussionRailAccordionItem(props: {
    tabKey: DiscussionRailTabKey;
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    subtitle: string;
    playwrightId: string;
    isOpen: boolean;
    isRecentlyOpened: boolean;
    /**
     * Which corpus this module is serving, when it has more than one. Emitted so a browser test can
     * assert the rail and the main-column panel agree on one screen without reading copy
     * (`DISCUSSION-1`, design §13.1) — it is the same value the heading is derived from, so the
     * attribute cannot report one thing while the title says another.
     */
    contentSource?: string;
    children: React.ReactNode;
}) {
    const { tabKey, icon: Icon, title, subtitle, playwrightId, isOpen, isRecentlyOpened, contentSource, children } = props;
    return (
        <AccordionItem
            value={tabKey}
            className="border-none"
            data-playwright-id={playwrightId}
            data-related-source={contentSource}
        >
            <div
                className={cn(
                    "overflow-hidden rounded-xl border border-border bg-card",
                    // The CLOSED state used to be `border-border/75` over a `/60` base, i.e. it was
                    // carrying the state in the border's ALPHA. With both converged to full strength
                    // (`DISCUSSION-6`) there is nothing above `border-border` to step up to, so the
                    // fill carries the closed state on its own — which it already did.
                    isOpen ? null : "bg-[color-mix(in_srgb,var(--card)_90%,var(--muted))]",
                )}
                data-home-reveal-effect="panel"
                data-home-reveal-open={isOpen ? "true" : "false"}
                data-home-reveal-phase={isRecentlyOpened ? "settling" : "idle"}
            >
                <AccordionTrigger
                    className="gap-3 px-4 py-4 hover:no-underline"
                    data-playwright-id={`discussion-rail-tab-${tabKey}`}
                >
                    <div className="flex min-w-0 items-start gap-3 text-left">
                        <div className={MODULE_ICON_TILE_CLASS} data-home-reveal-icon-surface="true">
                            <Icon className="size-4.5" />
                        </div>
                        <div className="min-w-0" data-home-reveal-copy="true">
                            <h2 className="text-sm font-semibold text-text-main">{title}</h2>
                            <p className="text-xs text-muted-foreground">{subtitle}</p>
                        </div>
                    </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">{children}</AccordionContent>
            </div>
        </AccordionItem>
    );
}

/**
 * The Related module's terminal-failure branch (rule 32, frontend `#F90`, 2026-08-15).
 *
 * `discussion.jte` server-renders the related-posts list via `components/crawlable_discussion_list.jte`,
 * and it sits inside the **`DiscussionPage`** island, in the main column — the rail's own `<aside>`
 * ships empty. `mountReactNode` uses `createRoot()`, so that section is discarded the moment
 * `DiscussionPage` mounts. On desktop the rail re-renders related posts from its own fetch; when the
 * fetch fails terminally, a bare retry button leaves the page with LESS than the HTML the server
 * shipped, which is the invariant rule 32 exists to hold.
 *
 * So the server's own list goes back first (`SsrRestoredList` — the single shared implementation, and
 * it carries rule 32's third-door marker `data-island-list-restored="true"` that
 * `tests/helpers/islandHealthGuard.mjs` asserts on). The retry stays below it: restoring the markup
 * must not cost the reader the way back to a live rail. `SsrRestoredList` renders nothing when there
 * is no snapshot, so a surface with no server-rendered counterpart degrades to exactly the previous
 * retry-only behaviour.
 */
function RelatedRailFailure(props: { localizer: Localizer; onRetry: () => void }) {
    const fallback = crawlableDiscussionListFallback("DiscussionPage");
    return (
        <div className="space-y-3">
            <SsrRestoredList componentName={fallback.componentName} selector={fallback.selector} />
            <RailRetry localizer={props.localizer} onRetry={props.onRetry} />
        </div>
    );
}

function RailRetry(props: { localizer: Localizer; onRetry: () => void }) {
    return (
        <button
            type="button"
            onClick={props.onRetry}
            // The hover used to be `hover:border-border/70` over a `/55` base. Converging the base to
            // full strength (`DISCUSSION-6`) leaves no stronger border to hover to, so the hover moves
            // to `card-hover` — the design language's one hover wash, already used by RAIL_ROW_CLASS.
            className="w-full rounded-xl border border-border bg-muted/40 px-3 py-2 text-xs font-medium text-neutral-text transition-colors card-hover hover:text-text-main"
            data-playwright-id="discussion-rail-retry"
        >
            {props.localizer("literal.retry")}
        </button>
    );
}

// --- Related discussions ---------------------------------------------------

// Pixel size of the rail thumbnail — matches the Groups/Series rows' leading 48px tile so all three
// rail modules share one rhythm. Passed to <img> width/height as well so the slot is reserved before
// the bytes decode and the rail cannot shift as images load.
const RAIL_THUMBNAIL_PX = 48;

/**
 * The picture to show beside a related discussion, or null when it should show none.
 *
 * Order: the author-chosen cover (`imageUrl`), else the first image in `cardMediaItems` — the same
 * parser the feed card uses, so the rail and the card agree on what a discussion's picture is.
 * Returns null for sensitive/spoiler discussions: a 48px tile is too small to carry a real masking
 * affordance, so those simply render no thumbnail (masking by omission, which cannot leak a frame).
 * A row with no resolvable image renders exactly as it did before thumbnails existed.
 *
 * The URL is returned unsized — `SafeImage` applies THUMBNAIL_IMAGE_SIZE (and skips non-Seavien
 * hosts, e.g. a YouTube preview frame, which must be used as-is).
 */
export function resolveRelatedDiscussionThumbnailUrl(discussion: DiscussionWithoutContentDTO): string | null {
    const withMedia = discussion as DiscussionWithoutContentDTO & {
        cardMediaItems?: string | null;
        sensitive?: boolean | null;
        spoiler?: boolean | null;
    };
    if (withMedia.sensitive || withMedia.spoiler) {
        return null;
    }

    const coverUrl = discussion.imageUrl?.trim();
    if (coverUrl) {
        return coverUrl;
    }

    const firstImage = parseDiscussionCardMediaItems(withMedia.cardMediaItems).find((item) => item.kind === "image");
    return firstImage?.previewImageUrl?.trim() || firstImage?.sourceUrl?.trim() || null;
}

function RelatedDiscussionRow(props: { row: RelatedRailRow; localizer: Localizer }) {
    const { row, localizer } = props;
    const discussion = row.discussion;
    const target = discussion.slug ?? discussion.publicId;
    const href = target ? `/discussion/${encodeURIComponent(target)}` : undefined;
    const authorLabel = discussion.authorUsername ? getDisplayUsername(discussion.authorUsername, localizer) : null;
    const thumbnailUrl = React.useMemo(() => resolveRelatedDiscussionThumbnailUrl(discussion), [discussion]);

    const text = (
        <>
            {authorLabel ? (
                <div className="text-xs font-semibold text-muted-foreground wrap-anywhere">{authorLabel}</div>
            ) : null}
            <div className="mt-0.5 text-sm font-semibold leading-5 text-text-main line-clamp-2 wrap-anywhere">
                {discussion.title}
            </div>
            <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                    <MessageSquare className="size-3.5" />
                    {toFiniteCount(discussion.argumentsCount)}
                </span>
                <span className="ml-auto font-medium text-primary">{localizer("discussion.rail.related.read")} →</span>
            </div>
        </>
    );

    // The thumbnail is additive: with no image the row renders exactly its previous single-column
    // layout, so the module never depends on an element that may not render.
    const body = thumbnailUrl ? (
        <div className="flex items-start gap-3">
            <div className="shrink-0" data-playwright-id="discussion-rail-related-thumbnail">
                <SafeImage
                    src={thumbnailUrl}
                    alt=""
                    seavienImageSize={THUMBNAIL_IMAGE_SIZE}
                    width={RAIL_THUMBNAIL_PX}
                    height={RAIL_THUMBNAIL_PX}
                    className="size-12 rounded-xl border border-border object-cover"
                />
            </div>
            <div className="min-w-0 flex-1">{text}</div>
        </div>
    ) : (
        text
    );

    const hasActions = Boolean(discussion.publicId);

    return (
        <div className={RAIL_ROW_CLASS} data-related-row-source={row.source}>
            {href ? (
                <a
                    href={href}
                    className="block"
                    data-playwright-id="discussion-rail-related-row"
                    onClick={() => onDiscoveryCardActivated({
                        targetType: "discussion",
                        surface: "related",
                        // The durable half: becomes `discussion_views.source = 'related_rail'` on the
                        // destination page, covering the anonymous readers GA4 cannot see.
                        discussionPublicId: discussion.publicId,
                    })}
                >
                    {body}
                </a>
            ) : (
                <div data-playwright-id="discussion-rail-related-row">{body}</div>
            )}
            {hasActions ? (
                <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-border pt-2.5">
                    <DiscussionVoteButtons
                        discussionPublicId={discussion.publicId!}
                        initiallyAgreeing={Boolean(discussion.isUserAgreeing)}
                        initiallyDisagreeing={Boolean(discussion.isUserDisagreeing)}
                        initialAgreementsCount={toFiniteCount(discussion.agreementsCount)}
                        initialDisagreementsCount={toFiniteCount(discussion.disagreementsCount)}
                        localizer={localizer}
                        accountAccessSurface="discussion_rail_react"
                    />
                    <DiscussionBookmarkToggle
                        discussionPublicId={discussion.publicId!}
                        discussionSlug={discussion.slug ?? undefined}
                        initiallySaved={Boolean(discussion.isAddedToBookmarksSeries)}
                        localizer={localizer}
                        playwrightId="discussion-rail-related-bookmark"
                    />
                </div>
            ) : null}
        </div>
    );
}

// --- Series ----------------------------------------------------------------

function DiscussionSeriesRow(props: { series: SeriesDTO; localizer: Localizer }) {
    const { series, localizer } = props;
    const target = series.slug ?? series.publicId;
    const href = target ? `/series/${encodeURIComponent(target)}` : undefined;
    const title = getUserFacingSeriesTitle(series, localizer);
    // Real ICU plural rather than `{n} {localizer("series.discussions")}`, which rendered "1 posts"
    // (`DISCUSSION-20`) and cannot be made correct at all in a locale with more than two plural
    // forms, because the count and the noun are separate tokens. `null` at zero, the convention
    // `SearchGroupResultRow` already used.
    const discussionsLabel = getFriendlyCountBadgeLabel(localizer, "badge.series.discussions", series.discussionsCount);
    const canFollow = Boolean(series.publicId) && !series.isViewerOwner;

    const body = (
        <div className="flex items-start gap-3">
            <ProfileImage localizer={localizer}
                imageUrl={series.thumbnailUrl}
                username={title}
                mode="group"
                hoverable={false}
                // `shape` is what actually squares the photo: a rounded-xl className styles the
                // wrapper only, leaving a circular image inside it.
                shape="rounded"
                className="size-12 shrink-0"
            />
            <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold leading-5 text-text-main line-clamp-2 wrap-anywhere">{title}</div>
                {discussionsLabel ? (
                    <div className="mt-1 text-xs text-muted-foreground">{discussionsLabel}</div>
                ) : null}
            </div>
        </div>
    );

    return (
        <div className={RAIL_ROW_CLASS}>
            {href ? (
                <a
                    href={href}
                    className="block"
                    data-playwright-id="discussion-rail-series-row"
                    onClick={() => onDiscoveryCardActivated({ targetType: "series", surface: "related" })}
                >
                    {body}
                </a>
            ) : (
                <div data-playwright-id="discussion-rail-series-row">{body}</div>
            )}
            {canFollow ? (
                <div className="mt-2.5 flex justify-end border-t border-border pt-2.5">
                    <SeriesButtons
                        seriesPublicId={series.publicId!}
                        seriesSlug={series.slug ?? target ?? series.publicId!}
                        initiallyOwner={Boolean(series.isViewerOwner)}
                        initiallyFollowing={Boolean(series.isViewerFollowing)}
                        seriesTitle={series.title ?? ""}
                        seriesDescription={series.description ?? ""}
                        seriesThumbnailUrl={series.thumbnailUrl ?? ""}
                        mainLocale={series.mainLocale ?? undefined}
                        privateAccess={Boolean(series.privateAccess)}
                        bookmarksCollection={Boolean(series.bookmarksCollection)}
                        sensitive={Boolean(series.sensitive)}
                        allowWrap={false}
                        displayMode="primary-only"
                        surface="discussion_rail"
                        localizer={localizer}
                    />
                </div>
            ) : null}
        </div>
    );
}

// --- Composer --------------------------------------------------------------

export function DiscussionRail(props: {
    discussionPublicId: string | undefined;
    localizer: Localizer;
    seedGroups?: GroupDTO[];
    /**
     * Suppress every rail fetch. Set for a DELETED discussion, whose tombstone still mounts this
     * island: all three endpoints answer 400 ("Deleted posts are no longer available for this
     * action") because the backend is right to refuse them, so firing them produces nothing but
     * origin load and FRONTEND error noise -- ~30 refusals in 12h across 7 sessions and 6 named
     * members, observed 2026-08-01.
     *
     * `data-deleted` was already on the wire and already read into DiscussionPage; it simply never
     * reached the fetch condition, because `enabled` below has only ever meant "is this desktop".
     * An explicit prop rather than passing a falsy id: `useAsyncList` short-circuits on a falsy id
     * too, but that reads as an accident and would not survive a refactor of this component.
     */
    disabled?: boolean;
    /**
     * The section the main column is currently showing (`DISCUSSION-38`, wave-12 sheet Q19 option A).
     *
     * <p>The rail YIELDS the module the active section already serves. On `/related`, `/series` and
     * `/groups` the rail's module and the main column's panel were the same list in the same order —
     * `RAIL_PAGE_SIZE` is 10 and the panel paginates, so the rail is exactly a prefix of it. On a 1440px
     * viewport that spent 31% of the width restating the main column and pushed the two modules the
     * reader CANNOT already see below the fold.
     *
     * <p>Suppression is a conditional render, not a layout decision: every module already carries a
     * `visible` flag (Related and Series hide whenever empty), `availableTabs` filters on it,
     * `availableTabKeys` prunes `openTabs`, and the component returns `null` at zero. The `<aside>` sits
     * in a fixed `28rem` grid column, so the main column does not move either way.
     *
     * <p>The FETCHES are deliberately left alone. They feed a module that comes straight back when the
     * reader changes section, and gating them here would trade a duplicated render for a refetch on
     * every section change.
     */
    activeSection?: string;
}) {
    const { discussionPublicId, localizer, seedGroups, disabled, activeSection } = props;

    // Desktop-only, and the gate is the FETCH gate too: below `lg` this component renders nothing and
    // asks for nothing. It briefly had a second, inline mobile placement (`DISCUSSION-32`, 2026-08-25)
    // that put the same accordion between the article and the conversation list on a phone; that block
    // was measured at 1.5-2.5 viewports of scroll between a post and its own comments and removed on
    // 2026-08-30. The group ACTION it was really there for was moved onto the summary line above the
    // title the same day and REVERTED on 2026-08-31 (an attribution line is not an action surface), so
    // a phone now has no group action on this page at all — deliberately, and it is not this
    // component's job to put one back below `lg`. See §11 of
    // designs/01-now/2026-08-30-discussion-mobile-context-block-collapse-design.md.
    const isDesktop = useIsDesktopViewport();
    const enabled = isDesktop && Boolean(discussionPublicId) && !disabled;

    const relatedState = useRelatedDiscussions(enabled, discussionPublicId);
    const groupsState = useDiscussionGroupsRail(enabled, discussionPublicId, seedGroups ?? []);
    const seriesState = useDiscussionSeries(enabled, discussionPublicId);

    // Accordion state, mirroring the Home rail: every available module opens by default, the
    // user can collapse any of them, and a newly-opened panel briefly plays the settle animation.
    // Initialize OPEN with the full module set (all three are loading — and therefore visible —
    // on first desktop paint) so the panels render expanded from first paint instead of mounting
    // collapsed and then expanding via the reconcile effect below. That mount-collapsed→expand step
    // was a post-paint layout shift (desktop-discussion CLS, CWV Track 1 item 5 / §2.5 reflow (b));
    // the effect still prunes any module that later turns out hidden.
    const [openTabs, setOpenTabs] = React.useState<DiscussionRailTabKey[]>(() => ["related", "groups", "series"]);
    const [recentlyOpenedTabKey, setRecentlyOpenedTabKey] = React.useState<DiscussionRailTabKey | null>(null);
    const revealTimeoutRef = React.useRef<number | null>(null);

    const tabs = React.useMemo(() => {
        // Related and Series hide entirely when empty; Groups always renders (every discussion
        // belongs to at least one group, and an empty result shows a muted line).
        // `DISCUSSION-38`: a module also yields when the main column's active section already serves it.
        const relatedVisible = (relatedState.loading || relatedState.error || relatedState.items.length > 0)
            && activeSection !== "related";
        const seriesVisible = (seriesState.loading || seriesState.error || seriesState.items.length > 0)
            && activeSection !== "series";
        const groupsVisible = activeSection !== "groups";

        // ONE value decides both the copy and the rows. With no rows yet (skeleton, retry) nothing is
        // being claimed, and the module keeps its own identity until the answer arrives.
        const relatedSource = resolveRelatedRailSource(relatedState.items);
        const relatedCopy = RELATED_RAIL_MODULE_COPY[relatedSource];
        return [
            {
                key: "related" as const,
                icon: MessageSquare,
                title: localizer(relatedCopy.titleKey),
                subtitle: localizer(relatedCopy.subtitleKey),
                playwrightId: "discussion-rail-related",
                contentSource: relatedSource,
                visible: relatedVisible,
                content: relatedState.loading ? (
                    <RailSkeletonRows rows={4} tile="rounded" />
                ) : relatedState.error ? (
                    <RelatedRailFailure localizer={localizer} onRetry={relatedState.reload} />
                ) : (
                    <div className="space-y-3">
                        {relatedState.items.map((row) => (
                            <RelatedDiscussionRow
                                key={row.discussion.publicId ?? row.discussion.slug}
                                row={row}
                                localizer={localizer}
                            />
                        ))}
                    </div>
                ),
            },
            {
                key: "groups" as const,
                icon: Users,
                title: localizer("discussion.rail.groups.title"),
                subtitle: localizer("discussion.rail.groups.subtitle"),
                playwrightId: "discussion-rail-groups",
                // Single-corpus module: it has no fallback, so there is nothing to disclose.
                contentSource: undefined as RelatedRailSource | undefined,
                // Groups otherwise always renders (every discussion belongs to at least one group, and
                // an empty result shows a muted line) — `DISCUSSION-38` is its only suppressor.
                visible: groupsVisible,
                content: groupsState.loading && groupsState.items.length === 0 ? (
                    <RailSkeletonRows rows={3} tile="rounded" />
                ) : groupsState.error && groupsState.items.length === 0 ? (
                    <RailRetry localizer={localizer} onRetry={groupsState.reload} />
                ) : groupsState.items.length === 0 ? (
                    <p className="text-xs text-neutral-text">{localizer("discussion.rail.groups.empty")}</p>
                ) : (
                    // `RecommendedGroupCard` at `layout="rail"` renders a flush row with no surface of its
                    // own (2026-08-09), so the list needs the shared hairline wrapper rather than gaps —
                    // otherwise the rows read as one undifferentiated block. This rail and the home rail
                    // share the component, so they share the treatment.
                    <div className={HOME_RAIL_ROW_LIST_CLASS}>
                        {groupsState.items.map((group) => (
                            <RecommendedGroupCard
                                key={group.publicId ?? group.slug ?? group.name ?? "group"}
                                group={group}
                                localizer={localizer}
                                surface="related"
                            />
                        ))}
                    </div>
                ),
            },
            {
                key: "series" as const,
                icon: Layers,
                title: localizer("discussion.rail.series.title"),
                subtitle: localizer("discussion.rail.series.subtitle"),
                playwrightId: "discussion-rail-series",
                contentSource: undefined as RelatedRailSource | undefined,
                visible: seriesVisible,
                content: seriesState.loading ? (
                    <RailSkeletonRows rows={2} tile="rounded" />
                ) : seriesState.error ? (
                    <RailRetry localizer={localizer} onRetry={seriesState.reload} />
                ) : (
                    <div className="space-y-3">
                        {seriesState.items.map((series) => (
                            <DiscussionSeriesRow key={series.publicId ?? series.slug ?? series.title} series={series} localizer={localizer} />
                        ))}
                    </div>
                ),
            },
        ];
    }, [relatedState, groupsState, seriesState, localizer, activeSection]);

    const availableTabs = React.useMemo(() => tabs.filter((tab) => tab.visible), [tabs]);
    // `tabs` (and thus `availableTabs`) is a fresh array on every render because the underlying
    // loadable states are new objects each render. Key the visible-tab list on its contents so
    // `availableTabKeys` keeps a stable reference until the set of visible tabs actually changes —
    // otherwise the open-tabs effect below would re-run every render and loop forever.
    const availableTabKeysSignature = availableTabs.map((tab) => tab.key).join("|");
    const availableTabKeys = React.useMemo<DiscussionRailTabKey[]>(
        () => availableTabs.map((tab) => tab.key),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [availableTabKeysSignature],
    );

    // The denominator for `discovery_card_clicked{surface=related}`. One impression per module per page
    // instance; see designs/01-now/2026-08-07-recommendation-surface-measurement-design.md §Decision 1.
    // `enabled` is part of readiness so a reader below `lg` never enters the denominator: this
    // component renders nothing and fetches nothing there, and an impression counted for a rail that
    // never painted is a CTR regression that is really a measurement bug.
    const isModuleReady = React.useCallback(
        <T,>(key: DiscussionRailTabKey, state: { loading: boolean; error?: unknown }) =>
            enabled && openTabs.includes(key) && !state.loading && !state.error,
        [enabled, openTabs],
    );
    useDiscoveryRailImpression({
        targetType: "discussion",
        surface: "related",
        itemCount: relatedState.items.length,
        ready: isModuleReady("related", relatedState),
    });
    useDiscoveryRailImpression({
        targetType: "group",
        surface: "related",
        itemCount: groupsState.items.length,
        ready: isModuleReady("groups", groupsState),
    });
    useDiscoveryRailImpression({
        targetType: "series",
        surface: "related",
        itemCount: seriesState.items.length,
        ready: isModuleReady("series", seriesState),
    });

    const clearRevealTimeout = React.useCallback(() => {
        if (revealTimeoutRef.current != null) {
            window.clearTimeout(revealTimeoutRef.current);
            revealTimeoutRef.current = null;
        }
    }, []);

    React.useEffect(() => clearRevealTimeout, [clearRevealTimeout]);

    React.useEffect(() => {
        setOpenTabs((previous) => {
            const previousSet = new Set(previous);
            const normalizedPrevious = previous.filter((key) => availableTabKeys.includes(key));
            const next = [...normalizedPrevious];

            for (const key of availableTabKeys) {
                if (!previousSet.has(key)) {
                    next.push(key);
                }
            }

            // Return the existing array when nothing changed so React can bail out of the update.
            // Returning a fresh-but-equal array on every run would re-render endlessly.
            const unchanged = next.length === previous.length && next.every((key, index) => key === previous[index]);
            return unchanged ? previous : next;
        });
    }, [availableTabKeys]);

    const handleOpenTabsChange = React.useCallback((nextOpenTabs: string[]) => {
        const normalizedOpenTabs = nextOpenTabs.filter((key): key is DiscussionRailTabKey => availableTabKeys.includes(key as DiscussionRailTabKey));
        setOpenTabs((previous) => {
            const previousSet = new Set(previous);
            const newlyOpenedTabKey = normalizedOpenTabs.find((key) => !previousSet.has(key)) ?? null;

            clearRevealTimeout();
            if (newlyOpenedTabKey) {
                setRecentlyOpenedTabKey(newlyOpenedTabKey);
                revealTimeoutRef.current = window.setTimeout(() => {
                    setRecentlyOpenedTabKey(null);
                    revealTimeoutRef.current = null;
                }, DISCUSSION_RAIL_REVEAL_DURATION_MS);
            } else {
                setRecentlyOpenedTabKey(null);
            }

            return normalizedOpenTabs;
        });
    }, [availableTabKeys, clearRevealTimeout]);

    // Desktop only, and `enabled` above is the same predicate — nothing renders and nothing is
    // fetched below `lg`.
    if (!discussionPublicId || !isDesktop || availableTabs.length === 0) {
        return null;
    }

    // The parent <aside> makes the rail sticky (lg: sticky/self-start + max-h/overflow-y-auto),
    // so it stays pinned beside the comment thread as the reader scrolls, and scrolls internally
    // when all modules are open and exceed the viewport. px-6/pb-6 padding gives each panel's
    // soft shadow room so it is not clipped.
    return (
        <div className="px-6 pb-6" data-playwright-id="discussion-rail">
            <Accordion type="multiple" value={openTabs} onValueChange={handleOpenTabsChange} className="space-y-4">
                {availableTabs.map((tab) => (
                    <DiscussionRailAccordionItem
                        key={tab.key}
                        tabKey={tab.key}
                        icon={tab.icon}
                        title={tab.title}
                        subtitle={tab.subtitle}
                        playwrightId={tab.playwrightId}
                        contentSource={tab.contentSource}
                        isOpen={openTabs.includes(tab.key)}
                        isRecentlyOpened={recentlyOpenedTabKey === tab.key}
                    >
                        {tab.content}
                    </DiscussionRailAccordionItem>
                ))}
            </Accordion>
        </div>
    );
}

export default DiscussionRail;
